// Response handling utilities for API calls
export class ApiResponse {
  constructor(response) {
    this.response = response;
    this.data = response.data;
  }

  get success() {
    return this.data?.success === true;
  }

  get message() {
    return this.data?.message || '';
  }

  get payload() {
    return this.data?.data;
  }

  get error() {
    return this.data?.error;
  }

  get errorCode() {
    return this.data?.error?.code;
  }

  get errorMessage() {
    return this.data?.error?.message;
  }

  get errorDetails() {
    return this.data?.error?.details;
  }

  // Check if response is successful
  isSuccess() {
    return this.success;
  }

  // Check if response has error
  hasError() {
    return !this.success && this.error;
  }

  // Get user-friendly error message
  getErrorMessage() {
    if (this.hasError()) {
      return this.errorMessage || this.message || 'Unknown error occurred';
    }
    return null;
  }

  // Get detailed error information
  getErrorDetails() {
    if (this.hasError()) {
      return {
        code: this.errorCode,
        message: this.errorMessage,
        details: this.errorDetails
      };
    }
    return null;
  }
}

// Error handling utilities
export const ErrorCodes = {
  // Input validation errors
  INVALID_REQUEST: 'INVALID_REQUEST',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  INVALID_ID: 'INVALID_ID',
  INVALID_QUERY_PARAMS: 'INVALID_QUERY_PARAMS',
  EMPTY_BATCH: 'EMPTY_BATCH',
  BATCH_SIZE_EXCEEDED: 'BATCH_SIZE_EXCEEDED',

  // Authentication errors
  UNAUTHORIZED: 'UNAUTHORIZED',
  TOKEN_NOT_FOUND: 'TOKEN_NOT_FOUND',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  INVALID_API_TOKEN: 'INVALID_API_TOKEN',
  TOKEN_VALIDATION_FAILED: 'TOKEN_VALIDATION_FAILED',
  INSUFFICIENT_PERMISSION: 'INSUFFICIENT_PERMISSION',

  // Resource errors
  NOT_FOUND: 'NOT_FOUND',
  DATA_NOT_FOUND: 'DATA_NOT_FOUND',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  ALREADY_EXISTS: 'ALREADY_EXISTS',
  DUPLICATE_DATA: 'DUPLICATE_DATA',

  // Operation errors
  CREATE_FAILED: 'CREATE_FAILED',
  UPDATE_FAILED: 'UPDATE_FAILED',
  DELETE_FAILED: 'DELETE_FAILED',
  GET_DATA_FAILED: 'GET_DATA_FAILED',
  LIST_DATA_FAILED: 'LIST_DATA_FAILED',
  BATCH_SAVE_FAILED: 'BATCH_SAVE_FAILED',
  DUPLICATE_CHECK_FAILED: 'DUPLICATE_CHECK_FAILED',
  INVALID_UPDATED_DATA: 'INVALID_UPDATED_DATA',

  // System errors
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  DATABASE_ERROR: 'DATABASE_ERROR',
  SERVICE_ERROR: 'SERVICE_ERROR'
};

// Error message mapping
export const ErrorMessages = {
  [ErrorCodes.INVALID_REQUEST]: '请求参数格式错误',
  [ErrorCodes.VALIDATION_FAILED]: '数据验证失败',
  [ErrorCodes.INVALID_ID]: 'ID格式无效',
  [ErrorCodes.INVALID_QUERY_PARAMS]: '查询参数无效',
  [ErrorCodes.EMPTY_BATCH]: '批量数据不能为空',
  [ErrorCodes.BATCH_SIZE_EXCEEDED]: '批量数据超出限制',

  [ErrorCodes.UNAUTHORIZED]: '未授权访问',
  [ErrorCodes.TOKEN_NOT_FOUND]: '认证令牌未找到',
  [ErrorCodes.TOKEN_EXPIRED]: '认证令牌已过期',
  [ErrorCodes.TOKEN_INVALID]: '认证令牌无效',
  [ErrorCodes.INVALID_API_TOKEN]: 'API令牌无效',
  [ErrorCodes.TOKEN_VALIDATION_FAILED]: '令牌验证失败',
  [ErrorCodes.INSUFFICIENT_PERMISSION]: '权限不足',

  [ErrorCodes.NOT_FOUND]: '资源未找到',
  [ErrorCodes.DATA_NOT_FOUND]: '数据不存在',
  [ErrorCodes.USER_NOT_FOUND]: '用户不存在',
  [ErrorCodes.ALREADY_EXISTS]: '资源已存在',
  [ErrorCodes.DUPLICATE_DATA]: '数据已存在',

  [ErrorCodes.CREATE_FAILED]: '创建失败',
  [ErrorCodes.UPDATE_FAILED]: '更新失败',
  [ErrorCodes.DELETE_FAILED]: '删除失败',
  [ErrorCodes.GET_DATA_FAILED]: '获取数据失败',
  [ErrorCodes.LIST_DATA_FAILED]: '获取列表失败',
  [ErrorCodes.BATCH_SAVE_FAILED]: '批量保存失败',
  [ErrorCodes.DUPLICATE_CHECK_FAILED]: '重复检查失败',
  [ErrorCodes.INVALID_UPDATED_DATA]: '更新数据无效',

  [ErrorCodes.INTERNAL_SERVER_ERROR]: '服务器内部错误',
  [ErrorCodes.DATABASE_ERROR]: '数据库错误',
  [ErrorCodes.SERVICE_ERROR]: '服务处理错误'
};

// Get user-friendly error message
export function getErrorMessage(errorCode, fallbackMessage = '操作失败') {
  return ErrorMessages[errorCode] || fallbackMessage;
}

// Handle API response
export function handleApiResponse(response) {
  const apiResponse = new ApiResponse(response);
  
  if (apiResponse.isSuccess()) {
    return {
      success: true,
      data: apiResponse.payload,
      message: apiResponse.message
    };
  } else {
    return {
      success: false,
      error: apiResponse.getErrorDetails(),
      message: apiResponse.getErrorMessage()
    };
  }
}

// Handle API error
export function handleApiError(error) {
  if (error.response) {
    const apiResponse = new ApiResponse(error.response);
    
    if (apiResponse.hasError()) {
      return {
        success: false,
        error: apiResponse.getErrorDetails(),
        message: apiResponse.getErrorMessage()
      };
    } else {
      // Fallback for non-standardized error responses
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.response.data?.error || error.response.data?.message || 'Unknown error'
        },
        message: error.response.data?.error || error.response.data?.message || 'Request failed'
      };
    }
  } else if (error.request) {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Network request failed'
      },
      message: '网络请求失败'
    };
  } else {
    return {
      success: false,
      error: {
        code: 'CLIENT_ERROR',
        message: error.message
      },
      message: '客户端错误'
    };
  }
}

// Notification helper for different error types
export function showErrorNotification(error, notification) {
  const errorCode = error?.error?.code;
  const message = error?.message || '操作失败';
  
  switch (errorCode) {
    case ErrorCodes.DUPLICATE_DATA:
      notification.warning({
        message: '数据重复',
        description: message,
        duration: 4
      });
      break;
      
    case ErrorCodes.VALIDATION_FAILED:
      notification.error({
        message: '验证失败',
        description: message,
        duration: 6
      });
      break;
      
    case ErrorCodes.UNAUTHORIZED:
    case ErrorCodes.TOKEN_EXPIRED:
    case ErrorCodes.TOKEN_INVALID:
      notification.error({
        message: '认证失败',
        description: '请重新登录',
        duration: 4
      });
      break;
      
    case ErrorCodes.INSUFFICIENT_PERMISSION:
      notification.error({
        message: '权限不足',
        description: message,
        duration: 4
      });
      break;
      
    default:
      notification.error({
        message: '操作失败',
        description: message,
        duration: 4
      });
  }
}

export default {
  ApiResponse,
  ErrorCodes,
  ErrorMessages,
  getErrorMessage,
  handleApiResponse,
  handleApiError,
  showErrorNotification
};